import React, { useState } from "react";
import useSWR from "swr";
import api, { money, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { EXP_CATEGORY } from "@/lib/labels";

const fetcher = (u) => api.get(u).then((r) => r.data);
const CATS = ["accommodation","vehicles","fuel","food","flights","guides","permits","activities","marketing","commissions","stripe","insurance","unexpected","other"];
const empty = { tour_id: "", category: "other", concept: "", date: new Date().toISOString().slice(0,10), amount: 0, currency: "USD", exchange_rate: 1 };

export default function Finances() {
  const { data: tours } = useSWR("/tours", fetcher);
  const { data: expenses, mutate } = useSWR("/expenses", fetcher);
  const [selTour, setSelTour] = useState("");
  const { data: finance } = useSWR(selTour ? `/tours/${selTour}/finance` : null, fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  React.useEffect(() => {
    if (tours && !selTour && tours[0]) setSelTour(tours[0].id);
  }, [tours, selTour]);

  const submit = async () => {
    if (!form.tour_id) return toast.error("Selecciona tour");
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount), exchange_rate: Number(form.exchange_rate) });
      toast.success("Gasto añadido");
      setForm(empty); setOpen(false); mutate();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const tourName = (id) => tours?.find((t) => t.id === id)?.name || "—";
  const filtered = (expenses || []).filter((e) => !selTour || e.tour_id === selTour);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Gastos & Rentabilidad</h1><p className="text-slate-500 mt-1">Análisis financiero por tour.</p></div>
        <div className="flex gap-2 items-center">
          <Select value={selTour} onValueChange={setSelTour}>
            <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-expense-btn" className="bg-[#026FCB] hover:bg-[#0358a5]"><Plus size={16} className="mr-2" />Nuevo gasto</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Nuevo gasto</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Tour</Label>
                  <Select value={form.tour_id} onValueChange={(v) => setForm({...form, tour_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>{(tours || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Concepto</Label><Input value={form.concept} onChange={(e) => setForm({...form, concept: e.target.value})} /></div>
                <div><Label>Categoría</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{EXP_CATEGORY[c] || c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fecha</Label><DateInputES value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></div>
                <div><Label>Importe</Label><Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></div>
                <div><Label>Moneda</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["USD","EUR","CLP","NOK","ISK","GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Tipo de cambio</Label><Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm({...form, exchange_rate: e.target.value})} /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submit}>Guardar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {finance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Ingresos previstos</div><div className="text-xl font-bold mt-1 text-emerald-600">{money(finance.expected_revenue)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Cobrado neto</div><div className="text-xl font-bold mt-1">{money(finance.net_collected)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500">Gastos estimados</div><div className="text-xl font-bold mt-1 text-amber-600">{money(finance.budgeted_expenses)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs mono uppercase text-slate-500 flex items-center gap-1"><TrendingUp size={12} />Beneficio previsto</div><div className={`text-xl font-bold mt-1 ${finance.gross_margin_expected >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(finance.gross_margin_expected)}</div></CardContent></Card>
        </div>
      )}

      <Card className="overflow-hidden"><CardHeader><CardTitle>Gastos estimados</CardTitle></CardHeader><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-100 dark:bg-[#1A2336]">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Tour</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Concepto</th>
              <th className="text-right px-4 py-3">Importe</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 dark:border-[#25334D]/50">
                <td className="px-4 py-3 mono">{fmtDate(e.date)}</td>
                <td className="px-4 py-3 text-slate-500">{tourName(e.tour_id)}</td>
                <td className="px-4 py-3">{e.category}</td>
                <td className="px-4 py-3">{e.concept}</td>
                <td className="px-4 py-3 text-right mono">{money(e.amount, e.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
